import {
  makeStyles,
  mergeClasses,
  tokens,
  Button,
  Tooltip,
} from "@fluentui/react-components";
import {
  HomeRegular,
  TaskListSquareLtrRegular,
  PeopleTeamRegular,
  SearchRegular,
  SettingsRegular,
  GridRegular,
  DataBarVerticalRegular,
  DocumentTableRegular,
  PersonSearchRegular,
  ChevronLeftRegular,
  ChevronRightRegular,
} from "@fluentui/react-icons";
import { useAtom } from "jotai";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { ReactElement } from "react";
import { sidebarCollapsedAtom } from "@/store";

const NAV_WIDTH_EXPANDED = "240px";
const NAV_WIDTH_COLLAPSED = "64px";
const HEADER_HEIGHT = "56px";

const useStyles = makeStyles({
  nav: {
    position: "fixed",
    top: HEADER_HEIGHT,
    insetInlineStart: "0",
    bottom: "0",
    display: "flex",
    flexDirection: "column",
    backgroundColor: tokens.colorNeutralBackground2,
    transitionProperty: "width",
    transitionDuration: "200ms",
    transitionTimingFunction: "ease",
    zIndex: 50,
    overflowX: "hidden",
    "::after": {
      content: '""',
      position: "absolute",
      top: "0",
      bottom: "0",
      insetInlineEnd: "0",
      width: "1px",
      backgroundColor: tokens.colorNeutralStroke2,
    },
  },
  navExpanded: {
    width: NAV_WIDTH_EXPANDED,
  },
  navCollapsed: {
    width: NAV_WIDTH_COLLAPSED,
  },
  navItems: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
    paddingTop: tokens.spacingVerticalM,
    paddingInlineStart: tokens.spacingHorizontalS,
    paddingInlineEnd: tokens.spacingHorizontalS,
  },
  navButton: {
    justifyContent: "flex-start",
    minWidth: "0",
    width: "100%",
  },
  navButtonCollapsed: {
    justifyContent: "center",
  },
  toggleArea: {
    display: "flex",
    justifyContent: "center",
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
  },
});

interface NavItemDef {
  key: string;
  path: string;
  labelKey: string;
  icon: ReactElement;
}

const NAV_ITEMS: NavItemDef[] = [
  { key: "home", path: "/home", labelKey: "nav.home", icon: <HomeRegular /> },
  {
    key: "queue",
    path: "/queue",
    labelKey: "nav.myQueue",
    icon: <TaskListSquareLtrRegular />,
  },
  {
    key: "team",
    path: "/team",
    labelKey: "nav.team",
    icon: <PeopleTeamRegular />,
  },
  {
    key: "dashboard",
    path: "/dashboard",
    labelKey: "nav.dashboard",
    icon: <DataBarVerticalRegular />,
  },
  {
    key: "accounts",
    path: "/accounts",
    labelKey: "nav.accounts",
    icon: <DocumentTableRegular />,
  },
  {
    key: "obligors",
    path: "/obligors",
    labelKey: "nav.obligors",
    icon: <PersonSearchRegular />,
  },
  {
    key: "search",
    path: "/search",
    labelKey: "nav.search",
    icon: <SearchRegular />,
  },
  {
    key: "components",
    path: "/admin/components",
    labelKey: "nav.components",
    icon: <GridRegular />,
  },
  {
    key: "admin",
    path: "/admin",
    labelKey: "nav.admin",
    icon: <SettingsRegular />,
  },
];

export function Navigation() {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useAtom(sidebarCollapsedAtom);
  const navigate = useNavigate();
  const location = useLocation();
  const styles = useStyles();

  return (
    <nav
      className={mergeClasses(
        styles.nav,
        collapsed ? styles.navCollapsed : styles.navExpanded,
      )}
    >
      <div className={styles.navItems}>
        {NAV_ITEMS.map((item) => {
          const isActive = location.pathname === item.path;
          const button = (
            <Button
              key={item.key}
              className={mergeClasses(
                styles.navButton,
                collapsed ? styles.navButtonCollapsed : undefined,
              )}
              icon={item.icon}
              appearance={isActive ? "primary" : "subtle"}
              onClick={() => navigate(item.path)}
            >
              {!collapsed ? t(item.labelKey) : undefined}
            </Button>
          );

          if (collapsed) {
            return (
              <Tooltip
                key={item.key}
                content={t(item.labelKey)}
                relationship="label"
                positioning="after"
              >
                {button}
              </Tooltip>
            );
          }

          return button;
        })}
      </div>
      <div className={styles.toggleArea}>
        <Button
          icon={collapsed ? <ChevronRightRegular /> : <ChevronLeftRegular />}
          appearance="subtle"
          onClick={() => setCollapsed(!collapsed)}
          aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}
        />
      </div>
    </nav>
  );
}
